/**
 *
 *  ContentScroller
 *  Component
 *
 *
 *  Created by Vladimir Shishlyannikov on 17/11/2022.
 *  Copyright (c) 2016-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useMemo } from "react";
import ContentScrollerBaseScroller from "../ContentScrollerBaseScroller/ContentScrollerBaseScroller";
import ContentScrollerControls from "../ContentScrollerControls/ContentScrollerControls";
import useContentScroller from "../../hooks/useContentScroller/useContentScroller";
import useContentScrollerItemVisibility from "../../hooks/useContentScrollerItemVisibility/useContentScrollerItemVisibility";

import useForwardRef from "../../hooks/useForwardRef/useForwardRef";

const ContentScroller = React.forwardRef(
  (
    {
      ariaDescriptionId = "carousel-description",
      ariaLabelId,
      ariaScrollerRole,
      autoRecalcOnMount,
      children,
      className,
      controls,
      description,
      header,
      itemTypeDescription,
      style,
      trailingControls,
      ...rest
    },
    outerRef
  ) => {
    const { stateManagementApi, scrollingContainerRef } = useContentScroller(),
      {
        getCurrentChangeset,
        getTotalCount,
        getVisibleCount,
        getVisibleItemIndexes,
        isVisible,
      } = useContentScrollerItemVisibility({
        autoRecalcOnMount,
        stateManagementApi,
      }),
      contentScroller = useMemo(
        () => ({
          ...stateManagementApi,
          getCurrentChangeset,
          getVisibleCount,
          getVisibleItemIndexes,
          getTotalCount,
          isVisible,
        }),
        [
          getCurrentChangeset,
          getTotalCount,
          getVisibleCount,
          getVisibleItemIndexes,
          isVisible,
          stateManagementApi,
        ]
      );

    useForwardRef(
      outerRef,
      contentScroller != null && contentScroller.scrollingContainer
        ? contentScroller
        : undefined
    );

    return (
      <div
        aria-describedby={ariaDescriptionId}
        aria-labelledby={
          header
            ? ariaLabelId != null
              ? ariaLabelId
              : "carousel-label"
            : ariaLabelId
        }
        className={`${className} content-scroller-grid`}
        role="group"
        style={style}
        {...rest}
      >
        {header && (
          <div id={ariaLabelId != null ? ariaLabelId : "carousel-label"}>
            {header}
          </div>
        )}

        {(!trailingControls || controls) && (
          <div>
            {controls != null ? (
              controls
            ) : (
              <ContentScrollerControls contentScrollerApi={contentScroller} />
            )}
          </div>
        )}
        <ContentScrollerBaseScroller
          ref={scrollingContainerRef}
          role={ariaScrollerRole}
        >
          {children}
        </ContentScrollerBaseScroller>
        {trailingControls && <div>{trailingControls}</div>}
      </div>
    );
  }
);

export default ContentScroller;
